/**
 * Arthaus exhibition hero — rotation (Phase 10, HD-HP-EXHIBIT)
 * Markup: sections/exhibition-hero.liquid
 *
 * Frame 1 is server-rendered (the LCP) and never touched until the first dissolve.
 * Frames 2..n come from the [data-hd-exhibit-frames] JSON and are only fetched after
 * `load` + idle, never on Save-Data. Rotation starts once frame 2 has decoded.
 *
 * Pauses on pointer hover / focus within the section, when the tab is hidden and when
 * the stage is scrolled out of view; the visible toggle pauses it for good (WCAG 2.2.2).
 * prefers-reduced-motion: no rotation at all (frame 1 stays, the toggle stays hidden).
 *
 * Emits `hd:exhibit-frame` {index, url} on the section so the living palette can follow.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  if (reduce || conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')) return;

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 1200);
  }
  function afterLoad(fn) {
    if (document.readyState === 'complete') idle(fn);
    else window.addEventListener('load', function () { idle(fn); }, { once: true });
  }

  function init(root) {
    var stage = root.querySelector('[data-hd-exhibit-stage]');
    var current = root.querySelector('[data-hd-exhibit-frame]');
    var dataEl = root.querySelector('[data-hd-exhibit-frames]');
    var caption = root.querySelector('[data-hd-exhibit-caption]');
    var toggle = root.querySelector('[data-hd-exhibit-toggle]');
    if (!stage || !current || !dataEl) return;

    var extra;
    try { extra = JSON.parse(dataEl.textContent) || []; } catch (e) { return; }
    if (!extra.length) return;

    var interval = (parseInt(root.getAttribute('data-interval'), 10) || 7) * 1000;
    var frames = [{ el: current, caption: caption ? caption.textContent : '', url: '' }];
    var index = 0;
    var timer = null;
    var userPaused = false, hovered = false, focused = false, offscreen = false;

    function build(f) {
      var img = new Image();
      img.alt = f.alt || '';
      img.width = f.w; img.height = f.h;
      img.sizes = current.sizes;
      img.srcset = f.srcset;
      img.src = f.src;
      img.className = 'hd-exhibit__frame absolute inset-0 w-full h-full object-cover object-bottom';
      img.decoding = 'async';
      img.setAttribute('aria-hidden', 'true');
      return img;
    }

    function running() { return !userPaused && !hovered && !focused && !offscreen && !document.hidden; }

    function schedule() {
      clearTimeout(timer);
      if (running() && frames.length > 1) timer = setTimeout(next, interval);
    }

    function show(i) {
      var from = frames[index], to = frames[i];
      if (!to || !to.ready) return;
      // Frame 1 is the LCP: it only drifts once it's being left, never before.
      to.el.style.setProperty('--hd-drift', interval + 'ms');
      to.el.classList.remove('is-drifting');
      stage.insertBefore(to.el, stage.querySelector('.hd-exhibit__light'));
      void to.el.offsetWidth;                       // commit opacity:0 before transitioning
      to.el.classList.add('is-current');
      from.el.classList.remove('is-current');
      requestAnimationFrame(function () { to.el.classList.add('is-drifting'); });
      from.el.setAttribute('aria-hidden', 'true');
      to.el.removeAttribute('aria-hidden');
      if (caption) {
        caption.style.opacity = '0';
        setTimeout(function () { caption.textContent = to.caption; caption.style.opacity = ''; }, 180);
      }
      index = i;
      root.dispatchEvent(new CustomEvent('hd:exhibit-frame', { bubbles: true, detail: { index: i, url: to.url } }));
    }

    function next() {
      var i = (index + 1) % frames.length;
      var tries = 0;
      while (!frames[i].ready && tries < frames.length) { i = (i + 1) % frames.length; tries++; }
      if (i !== index) show(i);
      schedule();
    }

    function setPaused(p) {
      userPaused = p;
      if (toggle) {
        toggle.setAttribute('aria-pressed', p ? 'true' : 'false');
        toggle.setAttribute('aria-label', p ? 'Play slideshow' : 'Pause slideshow');
        var pa = toggle.querySelector('[data-hd-exhibit-icon-pause]');
        var pl = toggle.querySelector('[data-hd-exhibit-icon-play]');
        if (pa) pa.hidden = p;
        if (pl) pl.hidden = !p;
      }
      schedule();
    }

    afterLoad(function () {
      extra.forEach(function (f) {
        var entry = { el: build(f), caption: f.caption, url: f.url, ready: false };
        frames.push(entry);
        (entry.el.decode ? entry.el.decode() : Promise.resolve()).then(function () {
          entry.ready = true;
          // Rotation begins once the next frame is decoded and ready to dissolve in.
          if (frames[1] === entry) {
            frames[0].ready = true;
            if (toggle) toggle.hidden = false;
            schedule();
          }
        }).catch(function () { /* a frame that fails to decode is simply skipped */ });
      });
    });

    if (toggle) toggle.addEventListener('click', function () { setPaused(!userPaused); });
    root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hovered = true; schedule(); } });
    root.addEventListener('pointerleave', function () { hovered = false; schedule(); });
    root.addEventListener('focusin', function () { focused = true; schedule(); });
    root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) { focused = false; schedule(); } });
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        offscreen = !entries[0].isIntersecting;
        schedule();
      }, { threshold: 0.25 }).observe(stage);
    }
  }

  function boot() { document.querySelectorAll('[data-hd-exhibit]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
